import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import * as yup from "yup";
import { FormField } from "component/shared/form-fields";
import { SettingsDialog } from "component/shared/settings-dialog";

interface Props {
	open: boolean;
	onClose: () => void;
	onAdd: (emails: string[]) => void;
	/** Addresses already on the client's list, so duplicates can be rejected up front. */
	existingEmails?: string[];
}

const emailSchema = yup.string().email();

export const AddRecipientsDialog: React.FC<Props> = ({
	open,
	onClose,
	onAdd,
	existingEmails = [],
}) => {
	const [emails, setEmails] = useState<string[]>([""]);
	const [showValidation, setShowValidation] = useState(false);

	useEffect(() => {
		if (open) {
			setEmails([""]);
			setShowValidation(false);
		}
	}, [open]);

	const errorFor = (value: string, index: number): string | undefined => {
		const trimmed = value.trim();
		if (!trimmed) {
			return "This field is required";
		}
		if (!emailSchema.isValidSync(trimmed)) {
			return "Invalid Email";
		}
		if (existingEmails.some((existing) => existing.toLowerCase() === trimmed.toLowerCase())) {
			return "This recipient has already been added";
		}
		const earlier = emails.slice(0, index).map((email) => email.trim().toLowerCase());
		if (earlier.includes(trimmed.toLowerCase())) {
			return "Duplicate email";
		}
		return undefined;
	};

	const hasErrors = emails.some((email, index) => errorFor(email, index));

	const handleAdd = () => {
		if (hasErrors) {
			setShowValidation(true);
			return;
		}
		onAdd(emails.map((email) => email.trim()));
	};

	return (
		<SettingsDialog
			open={open}
			title="Add recipients"
			onClose={onClose}
			confirmLabel="Add Recipients"
			onConfirm={handleAdd}
		>
			<Mui.Typography>
					Enter email addresses to notify for lead activity, including lead forwarding emails
					from third-party CRMs.
				</Mui.Typography>
				{emails.map((email, index) => (
					<Mui.Box key={index} display="flex" alignItems="flex-start" gap={1}>
						<Mui.Box flex={1}>
							<FormField
								label="Email"
								value={email}
								onChange={(event) =>
									setEmails(emails.map((current, i) => (i === index ? event.target.value : current)))
								}
								error={showValidation && !!errorFor(email, index)}
								helperText={showValidation ? errorFor(email, index) : undefined}
							/>
						</Mui.Box>
						{/* The first field is never removable — there is always at least one. */}
						{index > 0 && (
							<Mui.IconButton
								color="error"
								style={{ marginTop: 24 }}
								onClick={() => setEmails(emails.filter((_, i) => i !== index))}
							>
								<Icons.Trash2 size={18} />
							</Mui.IconButton>
						)}
					</Mui.Box>
				))}
				{/* Tertiary button: Semibold 16/20 teal, 4px after the icon, left-aligned. */}
				<Mui.Button
					variant="text"
					startIcon={<Icons.Plus size={16} />}
					sx={{
						alignSelf: "flex-start",
						textTransform: "none",
						padding: 0,
						minWidth: 0,
						fontSize: 16,
						fontWeight: 600,
						lineHeight: "20px",
						color: "custom.tertiaryAction",
						"& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
					}}
					onClick={() => setEmails([...emails, ""])}
				>
					Add another
				</Mui.Button>
		</SettingsDialog>
	);
};
